import { useState } from 'react'
import { Icon } from '@iconify/react'
import {
  addReadingCriterion,
  ensureReadingCriteria,
  MAX_READING_CRITERIA,
  MAX_READING_CRITERION_TERM_LENGTH,
  removeReadingCriterion,
  updateReadingCriterion,
} from '@/lib/raspadinha-campaigns'
import type { RaspadinhaCampaign } from '@/types/raspadinha-campaign'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import tableStyles from './RaspadinhaGanhadoresSection.module.css'
import styles from './RaspadinhaCriteriosLeituraSection.module.css'

type RaspadinhaCriteriosLeituraSectionProps = {
  form: RaspadinhaCampaign
  onFormChange: (next: RaspadinhaCampaign) => void
}

export function RaspadinhaCriteriosLeituraSection({
  form,
  onFormChange,
}: RaspadinhaCriteriosLeituraSectionProps) {
  const criteria = ensureReadingCriteria(form.reading_criteria)
  const [termInput, setTermInput] = useState('')
  const [addError, setAddError] = useState<string | null>(null)

  const canAddMore = criteria.length < MAX_READING_CRITERIA
  const trimmedInput = termInput.trim()

  const handleAddCriterion = () => {
    const result = addReadingCriterion(criteria, termInput)
    if (result.error) {
      setAddError(result.error)
      return
    }

    setAddError(null)
    setTermInput('')
    onFormChange({ ...form, reading_criteria: result.criteria })
  }

  const handleUpdateCriterion = (criterionId: string, term: string) => {
    setAddError(null)
    onFormChange({
      ...form,
      reading_criteria: updateReadingCriterion(criteria, criterionId, term),
    })
  }

  const handleRemoveCriterion = (criterionId: string) => {
    setAddError(null)
    onFormChange({
      ...form,
      reading_criteria: removeReadingCriterion(criteria, criterionId),
    })
  }

  return (
    <section className={editStyles.editSection} aria-labelledby="raspadinha-criterios-leitura">
      <h2 id="raspadinha-criterios-leitura" className={editStyles.editSectionTitle}>
        <Icon className={editStyles.editSectionTitleIcon} icon="mdi:file-search-outline" />
        Critérios de Leitura
      </h2>

      <div className={editStyles.editSectionBody}>
        <p className={`${editStyles.editLabelHint} ${tableStyles.summaryHint}`}>
          Cadastre os termos que serão consultados na leitura do cupom fiscal. Se as compras do
          cupom contiverem algum destes termos, o participante seguirá para a raspadinha com
          possibilidade de premiação.
        </p>

        <div className={styles.addCriterionRow}>
          <div className={styles.addCriterionField}>
            <label className={editStyles.editLabel} htmlFor="raspadinha-reading-criterion-term">
              Termo
            </label>
            <input
              id="raspadinha-reading-criterion-term"
              type="text"
              className={editStyles.editInput}
              value={termInput}
              maxLength={MAX_READING_CRITERION_TERM_LENGTH}
              placeholder="Ex: garfo inox"
              onChange={(event) => {
                setTermInput(event.target.value)
                setAddError(null)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleAddCriterion()
                }
              }}
            />
          </div>
          <button
            type="button"
            className={styles.addCriterionButton}
            onClick={handleAddCriterion}
            disabled={!trimmedInput || !canAddMore}
          >
            <Icon icon="mdi:plus" aria-hidden />
            Adicionar
          </button>
        </div>

        {addError ? (
          <p className={styles.addCriterionError} role="alert">
            {addError}
          </p>
        ) : null}

        {!canAddMore ? (
          <p className={`${editStyles.editLabelHint} ${tableStyles.summaryHint}`} role="status">
            Limite de {MAX_READING_CRITERIA} critérios de leitura atingido.
          </p>
        ) : null}

        <p className={`${editStyles.editLabelHint} ${tableStyles.summaryHint}`}>
          {criteria.length} termo(s) cadastrado(s).
        </p>

        {criteria.length === 0 ? (
          <p className={tableStyles.emptyState}>Nenhum termo cadastrado nesta campanha.</p>
        ) : (
          <div className={tableStyles.tableWrap}>
            <table className={tableStyles.dataTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Termo</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {criteria.map((criterion, index) => (
                  <tr key={criterion.id}>
                    <td>
                      <span className={tableStyles.cellText}>{index + 1}</span>
                    </td>
                    <td>
                      <input
                        type="text"
                        className={`${editStyles.editInput} ${styles.termInput}`}
                        value={criterion.term}
                        maxLength={MAX_READING_CRITERION_TERM_LENGTH}
                        aria-label={`Termo ${index + 1}`}
                        onChange={(event) =>
                          handleUpdateCriterion(criterion.id, event.target.value)
                        }
                      />
                    </td>
                    <td className={styles.actionsCell}>
                      <button
                        type="button"
                        className={styles.removeCriterionButton}
                        onClick={() => handleRemoveCriterion(criterion.id)}
                        aria-label={`Remover termo ${criterion.term || index + 1}`}
                      >
                        <Icon icon="mdi:trash-can-outline" aria-hidden />
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}
